import { t, type TKey } from "@/lib/i18n";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { createClient } from "@/lib/connect";
import { fetchAll } from "@/lib/services/catalog";
import { TagManagerService } from "@gen/tracker/v1/tag_manager_connect";
import {
  TagManagerSnippet,
  TagManagerSnippetKind,
  TagManagerPlacement,
  TagManagerScope,
  TagManagerSnippetStatus,
  TagManagerDestination,
} from "@gen/tracker/v1/tag_manager_pb";
import { Pagination, ResourceKind } from "@gen/tracker/v1/common_pb";

const client = createClient(TagManagerService);

export interface ListSnippetsParams {
  scopeKind?: ResourceKind;
  scopeResourceId?: string;
  enabledOnly?: boolean;
}

export const tagManagerKeys = {
  all: ["tag-manager"] as const,
  list: (params: ListSnippetsParams) => [...tagManagerKeys.all, "list", params] as const,
  detail: (id: string) => [...tagManagerKeys.all, "detail", id] as const,
};

/**
 * Every snippet under the filter, page after page: the admin list sorts and
 * pages them itself, so it needs all of them, not the first hundred.
 */
export function useAllSnippets(params: ListSnippetsParams = {}) {
  return useQuery({
    queryKey: [...tagManagerKeys.list(params), "all"] as const,
    queryFn: () =>
      fetchAll(async (offset, limit) => {
        const res = await client.listSnippets({
          scope: params.scopeKind
            ? new TagManagerScope({ kind: params.scopeKind, resourceId: params.scopeResourceId ?? "" })
            : undefined,
          enabledOnly: params.enabledOnly ?? false,
          pagination: new Pagination({ limit, offset }),
        });
        return { items: res.items, total: res.pagination?.total ?? res.items.length };
      }),
  });
}

export function useGetSnippet(id: string | null) {
  return useQuery({
    queryKey: tagManagerKeys.detail(id ?? ""),
    enabled: !!id,
    queryFn: async () => {
      const res = await client.getSnippet({ id: id! });
      return res.snippet!;
    },
  });
}

export function useCreateSnippet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (snippet: TagManagerSnippet) => {
      const res = await client.createSnippet({ snippet });
      return res.snippet!;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: tagManagerKeys.all });
    },
  });
}

export function useUpdateSnippet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (snippet: TagManagerSnippet) => {
      const res = await client.updateSnippet({ snippet });
      return res.snippet!;
    },
    onSuccess: (updated) => {
      void qc.invalidateQueries({ queryKey: tagManagerKeys.all });
      qc.setQueryData(tagManagerKeys.detail(updated.id), updated);
    },
  });
}

export function useDeleteSnippet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await client.deleteSnippet({ id });
      return id;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: tagManagerKeys.all });
    },
  });
}

export function usePublishSnippet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; note?: string }) => {
      const res = await client.publishSnippet({ id: input.id, note: input.note ?? "" });
      return res.snippet!;
    },
    onSuccess: (snippet) => {
      void qc.invalidateQueries({ queryKey: tagManagerKeys.all });
      qc.setQueryData(tagManagerKeys.detail(snippet.id), snippet);
    },
  });
}

export function useListVersions(snippetId: string | null) {
  return useQuery({
    queryKey: [...tagManagerKeys.all, "versions", snippetId ?? ""],
    enabled: !!snippetId,
    queryFn: async () => {
      const res = await client.listVersions({ snippetId: snippetId! });
      return res.items;
    },
  });
}

export function useRollbackSnippet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; targetVersion: number; note?: string }) => {
      const res = await client.rollbackSnippet({
        id: input.id,
        targetVersion: input.targetVersion,
        note: input.note ?? "",
      });
      return res.snippet!;
    },
    onSuccess: (snippet) => {
      void qc.invalidateQueries({ queryKey: tagManagerKeys.all });
      qc.setQueryData(tagManagerKeys.detail(snippet.id), snippet);
    },
  });
}

export function useTestFireDestination() {
  return useMutation({
    mutationFn: async (input: {
      snippet?: TagManagerSnippet;
      snippetId?: string;
      simulatedEvent?: Record<string, string>;
    }) => {
      const res = await client.testFireDestination({
        snippet: input.snippet,
        snippetId: input.snippetId ?? "",
        simulatedEvent: input.simulatedEvent ?? {},
      });
      return res;
    },
  });
}

// Status label table for the FE list and form badges. Mirrors the proto
// enum names exactly so a proto-regen surfaces as a compile error.
const SNIPPET_STATUS_LABEL: Partial<Record<number, TKey>> = {
  [TagManagerSnippetStatus.DRAFT]: "tm.status.draft",
  [TagManagerSnippetStatus.PUBLISHED]: "tm.status.published",
  [TagManagerSnippetStatus.ARCHIVED]: "tm.status.archived",
};

export function snippetStatusLabel(v: number): string {
  const key = SNIPPET_STATUS_LABEL[v];
  return key ? t(key) : "—";
}

const DESTINATION_LABEL: Partial<Record<number, TKey>> = {
  [TagManagerDestination.CUSTOM_HTTP]: "tm.dest.http",
  [TagManagerDestination.GA4_MP]: "tm.dest.ga4",
  [TagManagerDestination.TIKTOK_EVENTS]: "tm.dest.tiktok",
};

export function destinationLabel(v: number): string {
  const key = DESTINATION_LABEL[v];
  return key ? t(key) : "—";
}

export const DESTINATION_OPTIONS: TagManagerDestination[] = [
  TagManagerDestination.CUSTOM_HTTP,
  TagManagerDestination.GA4_MP,
  TagManagerDestination.TIKTOK_EVENTS,
];

// GA4 Consent Mode v2 categories (canonical list across destinations).
export const CONSENT_CATEGORIES = [
  "analytics_storage",
  "ad_storage",
  "ad_user_data",
  "ad_personalization",
  "personalization_storage",
  "functionality_storage",
  "security_storage",
] as const;

// FE-friendly label maps. Kept here so the Tag Manager page + any future
// dropdowns import from a single source. Mirrors the BE enum names exactly so
// proto-regen changes surface as compile errors here.
const SNIPPET_KIND_LABEL: Partial<Record<number, TKey>> = {
  [TagManagerSnippetKind.GTM]: "tm.kind.gtm",
  [TagManagerSnippetKind.PIXEL]: "tm.kind.pixel",
  [TagManagerSnippetKind.POSTBACK]: "tm.kind.postback",
  [TagManagerSnippetKind.CUSTOM_JS]: "tm.kind.js",
  [TagManagerSnippetKind.CUSTOM_HTML]: "tm.kind.html",
};

export function snippetKindLabel(v: number): string {
  const key = SNIPPET_KIND_LABEL[v];
  return key ? t(key) : "—";
}

const SNIPPET_PLACEMENT_LABEL: Partial<Record<number, TKey>> = {
  [TagManagerPlacement.HEAD]: "tm.place.head",
  [TagManagerPlacement.BODY_TOP]: "tm.place.bodyTop",
  [TagManagerPlacement.BODY_BOTTOM]: "tm.place.bodyBottom",
  [TagManagerPlacement.BEFORE_REDIRECT]: "tm.place.beforeRedirect",
};

export function snippetPlacementLabel(v: number): string {
  const key = SNIPPET_PLACEMENT_LABEL[v];
  return key ? t(key) : "—";
}

// Subset of ResourceKind that is valid as a snippet scope. Keeping this
// closed-set explicit on the FE prevents the dropdown from offering scopes
// the BE will reject (matches validScopeKind in the Go service).
export const SNIPPET_SCOPE_KINDS: ResourceKind[] = [
  ResourceKind.WORKSPACE,
  ResourceKind.TRAFFIC_SOURCE,
  ResourceKind.OFFER,
  ResourceKind.CAMPAIGN,
  ResourceKind.LANDER,
];

const SCOPE_KIND_LABEL: Partial<Record<number, TKey>> = {
  [ResourceKind.WORKSPACE]: "audit.kind.workspace",
  [ResourceKind.TRAFFIC_SOURCE]: "audit.kind.trafficSource",
  [ResourceKind.OFFER]: "audit.kind.offer",
  [ResourceKind.CAMPAIGN]: "audit.kind.campaign",
  [ResourceKind.LANDER]: "audit.kind.lander",
};

export function scopeKindLabel(v: number): string {
  const key = SCOPE_KIND_LABEL[v];
  return key ? t(key) : "—";
}

// Editor language to plug into Monaco. POSTBACK is conceptually URL/JSON but
// HTML highlighting matches the snippets users actually write (img/script).
export function editorLanguageForKind(kind: TagManagerSnippetKind): "html" | "javascript" {
  return kind === TagManagerSnippetKind.CUSTOM_JS ? "javascript" : "html";
}

export type {
  TagManagerSnippet,
  TagManagerSnippetKind,
  TagManagerPlacement,
  TagManagerScope,
  TagManagerSnippetStatus,
  TagManagerDestination,
};
